import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import { Card, CardContent, Button, Badge, EmptyState, Skeleton, ErrorState } from '@/components/ui'
import { Building2, Plus, Users, AppWindow, ArrowRight } from 'lucide-react'
import { api, type ConsoleOrgWithRole } from '@/api/client'

export function OrgsListPage() {
  const [organizations, setOrganizations] = useState<ConsoleOrgWithRole[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        setError(null)
        const response = await api.orgs.list()
        // Handle both 'orgs' (new) and 'organizations' (legacy) response formats
        setOrganizations(response.orgs || response.organizations || [])
      } catch (err) {
        console.error('Failed to fetch organizations:', err)
        setError(err instanceof Error ? err.message : 'Failed to load organizations')
      } finally {
        setIsLoading(false)
      }
    }

    fetchOrgs()
  }, [])

  if (isLoading) {
    return (
      <div>
        <PageHeader
          title="Organizations"
          description="Manage your organizations and apps"
          action={
            <Button disabled>
              <Plus className="mr-2 h-4 w-4" />
              Create Organization
            </Button>
          }
        />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-12 w-12 rounded-lg" />
                    <div>
                      <Skeleton className="h-4 w-24 mb-1" />
                      <Skeleton className="h-3 w-16" />
                    </div>
                  </div>
                  <Skeleton className="h-5 w-12" />
                </div>
                <div className="mt-4 flex items-center gap-4">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-16" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <PageHeader
          title="Organizations"
          description="Manage your organizations and apps"
          action={
            <Button asChild>
              <Link to="/orgs/new">
                <Plus className="mr-2 h-4 w-4" />
                Create Organization
              </Link>
            </Button>
          }
        />
        <ErrorState
          title="Failed to load organizations"
          description={error}
          action={
            <Button onClick={() => window.location.reload()}>
              Try Again
            </Button>
          }
        />
      </div>
    )
  }

  return (
    <div>
      <PageHeader
        title="Organizations"
        description="Manage your organizations and apps"
        action={
          <Button asChild>
            <Link to="/orgs/new">
              <Plus className="mr-2 h-4 w-4" />
              Create Organization
            </Link>
          </Button>
        }
      />

      {organizations.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="No organizations yet"
          description="Create your first organization to start building apps"
          action={
            <Button asChild>
              <Link to="/orgs/new">
                <Plus className="mr-2 h-4 w-4" />
                Create Organization
              </Link>
            </Button>
          }
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {organizations.map((item) => (
            <Card key={item.org.id} className="group">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[var(--bg-elevated-2)]">
                      {item.org.logo_url ? (
                        <img
                          src={item.org.logo_url}
                          alt={item.org.name}
                          className="h-8 w-8 object-contain"
                        />
                      ) : (
                        <Building2 className="h-6 w-6 text-[var(--text-secondary)]" />
                      )}
                    </div>
                    <div>
                      <h3 className="text-base font-medium text-[var(--text-primary)]">
                        {item.org.name}
                      </h3>
                      <p className="text-sm text-[var(--text-muted)]">{item.org.slug}</p>
                    </div>
                  </div>
                  <Badge>{item.role}</Badge>
                </div>

                <div className="mt-4 flex items-center gap-4 text-sm text-[var(--text-secondary)]">
                  <div className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    <span>Organization</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <AppWindow className="h-4 w-4" />
                    <span>{item.org.status}</span>
                  </div>
                </div>

                <Link
                  to={`/orgs/${item.org.slug}`}
                  className="mt-4 flex items-center gap-1 text-sm text-[var(--accent)] opacity-0 transition-opacity group-hover:opacity-100"
                >
                  Open dashboard <ArrowRight className="h-3 w-3" />
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
